/**
 * Preserve intended deep links through login and normalize legacy/shortcut paths.
 */

/** Shortcut paths that should resolve to a canonical in-app route. */
export const AUTH_ROUTE_ALIASES: Record<string, string> = {
  "/catalog": "/sales/catalogue",
  "/catalogue": "/sales/catalogue",
};

export function getCurrentPathWithSearch(): string {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}`;
}

/** Reject open redirects; allow only same-origin relative paths. */
export function sanitizeRedirectPath(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return "/";
  const pathOnly = trimmed.split("?")[0] ?? trimmed;
  if (pathOnly.toLowerCase() === "/login" || pathOnly.toLowerCase().startsWith("/login/")) {
    return "/";
  }
  if (pathOnly.toLowerCase() === "/staff-login" || pathOnly.toLowerCase().startsWith("/staff-login/")) {
    return "/";
  }
  return trimmed;
}

/** Apply alias map to pathname while keeping query string intact. */
export function resolveIntendedPath(raw: string): string {
  const safe = sanitizeRedirectPath(raw);
  const qIndex = safe.indexOf("?");
  const pathname = qIndex >= 0 ? safe.slice(0, qIndex) : safe;
  const search = qIndex >= 0 ? safe.slice(qIndex) : "";
  const canonical = AUTH_ROUTE_ALIASES[pathname] ?? pathname;
  return `${canonical}${search}`;
}

/** Login page URL that returns to `targetPath` (or current location) after sign-in. */
export function getLoginUrl(targetPath?: string): string {
  const dest = resolveIntendedPath(targetPath ?? getCurrentPathWithSearch());
  const loginPath =
    typeof window !== "undefined" &&
    import.meta.env.DEV &&
    import.meta.env.VITE_CLARITY_ONE_DEV === "true"
      ? "/staff-login"
      : "/login";
  if (dest === "/") return loginPath;
  return `${loginPath}?redirect=${encodeURIComponent(dest)}`;
}
